import type { ClockUser, StatusBreak, StatusPunch } from '@src/api/punchTypes';
import {
    elapsedSince,
    formatInstant
} from '@src/components/PunchPad/timeUtils';
import { Text } from '@7shifts/sous-chef-base';
import { useTranslation } from '@src/i18n';
import { CoffeeIllustration } from './icons/CoffeeIllustration';
import { TimerIcon } from './icons/TimerIcon';
import { UserChip } from './UserChip';
import './BreakView.css';

interface BreakViewProps {
    user: ClockUser;
    currentBreak: StatusBreak;
    punch: StatusPunch;
    serverTime: string;
}

export function BreakView({
    user,
    currentBreak,
    punch,
    serverTime
}: BreakViewProps) {
    const { t } = useTranslation();
    const elapsed = elapsedSince(currentBreak.startTime, serverTime);
    const timeOnShift = elapsedSince(punch.clockedInAt, serverTime);

    let subline = currentBreak.displayLabel;
    if (currentBreak.minLengthMinutes !== null) {
        subline += ` (${currentBreak.minLengthMinutes}m)`;
    }

    // Prefer the API-provided end time; fall back to deriving it from start + duration.
    const endTime =
        currentBreak.expectedEndTime ??
        (currentBreak.minLengthMinutes !== null
            ? new Date(
                  new Date(currentBreak.startTime).getTime() +
                      currentBreak.minLengthMinutes * 60_000
              ).toISOString()
            : null);

    if (endTime !== null) {
        subline += ` ${t('userStatus.breakEndsAt', {
            time: formatInstant(endTime)
        })}`;
    }

    return (
        <div className="break-view" data-testid="break-view">
            <UserChip user={user} statusLabel={t('userStatus.onBreak')} />

            <div className="break-view__content">
                <CoffeeIllustration className="break-view__coffee-icon" />
                <div className="break-view__text-stack">
                    <Text as="insight">
                        {t('userStatus.onBreakElapsed', { elapsed })}
                    </Text>
                    <Text as="h4">{subline}</Text>
                </div>
            </div>

            <div className="break-view__footer">
                <TimerIcon className="break-view__timer-icon" />
                <div className="break-view__footer-text">
                    <Text as="h3">{timeOnShift}</Text>
                    <Text as="h4">{t('userStatus.timeOnShift')}</Text>
                </div>
            </div>
        </div>
    );
}
